import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { isAdmin } from '../../utils/permissions';

interface HIPAAComplianceProps {
  userRole: string;
}

interface ComplianceItem {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'pending' | 'not-started';
  priority: 'high' | 'medium' | 'low';
  estimatedTime: string;
  cost: string;
}

export function HIPAACompliance({ userRole }: HIPAAComplianceProps) {
  const [complianceItems, setComplianceItems] = useState<ComplianceItem[]>([
    {
      id: 'aws-baa',
      title: 'AWS Business Associate Agreement',
      description: 'Sign AWS BAA to enable HIPAA-eligible services',
      status: 'not-started',
      priority: 'high',
      estimatedTime: '5 minutes',
      cost: 'FREE'
    },
    {
      id: 'database-encryption',
      title: 'Database Encryption Upgrade',
      description: 'Upgrade DynamoDB tables to customer-managed encryption',
      status: 'not-started',
      priority: 'high',
      estimatedTime: '2 hours',
      cost: '$50-200/month'
    },
    {
      id: 's3-encryption',
      title: 'S3 Bucket Encryption',
      description: 'Enable customer-managed encryption for document storage',
      status: 'not-started',
      priority: 'high',
      estimatedTime: '30 minutes',
      cost: '$10-50/month'
    },
    {
      id: 'audit-logging',
      title: 'Audit Logging',
      description: 'Enable CloudTrail for comprehensive audit logs',
      status: 'not-started',
      priority: 'medium',
      estimatedTime: '1 hour',
      cost: '$20-100/month'
    },
    {
      id: 'privacy-policy',
      title: 'Privacy Policy',
      description: 'Create HIPAA-compliant privacy policy',
      status: 'not-started',
      priority: 'medium',
      estimatedTime: '2 hours',
      cost: 'FREE'
    },
    {
      id: 'breach-response',
      title: 'Data Breach Response Plan',
      description: 'Document incident response procedures',
      status: 'not-started',
      priority: 'medium',
      estimatedTime: '1 hour',
      cost: 'FREE'
    },
    {
      id: 'staff-training',
      title: 'Staff Training',
      description: 'Train team on HIPAA requirements and security practices',
      status: 'not-started',
      priority: 'low',
      estimatedTime: '1 hour',
      cost: 'FREE'
    },
    {
      id: 'risk-assessment',
      title: 'Risk Assessment',
      description: 'Complete basic security risk assessment',
      status: 'not-started',
      priority: 'low',
      estimatedTime: '2 hours',
      cost: 'FREE'
    }
  ]);

  if (!isAdmin(userRole)) {
    return null;
  }

  const toggleItemStatus = (id: string) => {
    setComplianceItems(items =>
      items.map(item =>
        item.id === id
          ? {
              ...item,
              status: item.status === 'completed' ? 'pending' : 'completed'
            }
          : item
      )
    );
  };

  const completedCount = complianceItems.filter(item => item.status === 'completed').length;
  const totalCount = complianceItems.length;
  const completionPercentage = Math.round((completedCount / totalCount) * 100);

  const highPriorityPending = complianceItems.filter(
    item => item.priority === 'high' && item.status !== 'completed'
  ).length;

  return (
    <div className="space-y-6">
      {/* HIPAA Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                HIPAA Compliance Dashboard
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Track your progress toward HIPAA compliance
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{completionPercentage}%</div>
              <div className="text-sm text-gray-500">Complete</div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold text-green-600">{completedCount}</div>
                <div className="text-sm text-gray-500">Completed</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-red-600">{highPriorityPending}</div>
                <div className="text-sm text-gray-500">High Priority</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-600">{totalCount - completedCount}</div>
                <div className="text-sm text-gray-500">Remaining</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 pt-2">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Implementation Guide
              </Button>
              <Button variant="outline" size="sm">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                Share Progress
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Checklist */}
      <Card>
        <CardHeader>
          <CardTitle>Implementation Checklist</CardTitle>
          <p className="text-sm text-gray-600">
            Complete these items to achieve HIPAA compliance. Start with high-priority items.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {complianceItems.map((item) => (
              <div
                key={item.id}
                className={`p-4 border rounded-lg transition-all ${
                  item.status === 'completed'
                    ? 'bg-green-50 border-green-200'
                    : item.priority === 'high'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <button
                      onClick={() => toggleItemStatus(item.id)}
                      className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        item.status === 'completed'
                          ? 'bg-green-600 border-green-600 text-white'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {item.status === 'completed' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className={`font-medium ${
                          item.status === 'completed' ? 'text-green-800 line-through' : 'text-gray-900'
                        }`}>
                          {item.title}
                        </h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          item.priority === 'high'
                            ? 'bg-red-100 text-red-800'
                            : item.priority === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>⏱️ {item.estimatedTime}</span>
                        <span>💰 {item.cost}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cost Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">One-time Costs</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Implementation time</span>
                  <span>$500-1000</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>Total One-time</span>
                  <span>$500-1000</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Monthly Costs</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>KMS encryption</span>
                  <span>$50-200</span>
                </div>
                <div className="flex justify-between">
                  <span>S3 encryption</span>
                  <span>$10-50</span>
                </div>
                <div className="flex justify-between">
                  <span>Audit logging</span>
                  <span>$20-100</span>
                </div>
                <div className="flex justify-between font-medium border-t pt-1">
                  <span>Total Monthly</span>
                  <span>$80-350</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Total annual cost: $1,460-5,200</strong> - Very reasonable for HIPAA compliance compared to traditional solutions ($50k-200k).
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}